import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react';
import { sectionsAPI } from '../services/sectionsAPI';
import { formatAPIError } from '../utils/validation';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { isAuthenticated } = useAuth();

  // ✅ Memoized loader
  const loadNotifications = useCallback(
    async (onlyUnread = false) => {
      if (!isAuthenticated) {
        console.log('🔒 User not authenticated, skipping notifications load');
        return;
      }

      try {
        setLoading(true);
        setError('');
        console.log('🔔 Loading notifications...');

        const response = await sectionsAPI.notifications.getAll(onlyUnread);
        const data = response.data || [];

        setNotifications(data);

        const unread = data.filter((notif) => !notif.isRead);
        setUnreadCount(unread.length);

        console.log('✅ Notifications loaded:', data.length);
      } catch (error) {
        console.error('❌ Failed to load notifications:', error);
        setError(formatAPIError(error));
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated]
  );

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId) => {
      if (!isAuthenticated) return;

      try {
        console.log('📖 Marking notification as read:', notificationId);

        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notificationId
              ? { ...notif, isRead: true }
              : notif
          )
        );

        setUnreadCount((prev) => Math.max(0, prev - 1));

        await sectionsAPI.notifications.markAsRead(notificationId);
        console.log('✅ Notification marked as read:', notificationId);
      } catch (error) {
        console.error('❌ Failed to mark notification as read:', error);
        setError(formatAPIError(error));
      }
    },
    [isAuthenticated]
  );

  // Mark all as read
  const markAllAsRead = useCallback(
    async () => {
      if (!isAuthenticated) return;

      try {
        console.log('📖 Marking all notifications as read');

        setNotifications((prev) =>
          prev.map((notif) => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);

        await sectionsAPI.notifications.markAllAsRead();
        console.log('✅ All notifications marked as read');
      } catch (error) {
        console.error('❌ Failed to mark all notifications as read:', error);
        setError(formatAPIError(error));
      }
    },
    [isAuthenticated]
  );

  // ✅ Correct dependency array
  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
    }
  }, [isAuthenticated, loadNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    setError
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
